import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import { transactionsApi } from '../../api';
import {
  CreateWalletEntry,
  FundUserWalletParams,
  IPagination,
  IWalletTransactions,
  Transaction,
} from '../../types';
import { genFilterQuery } from '../../helpers';
import { toast } from 'react-toastify';

interface ITransactionState {
  loading: boolean;
  transactions: { data: Transaction[]; pagination: IPagination | null };
  walletTransactions: {
    data: IWalletTransactions[];
    pagination: IPagination | null;
  };
  walletAnalytics: {
    data: {
      totalFundsInflow: number;
      totalFundsOutflow: number;
      totalOutstandingWalletBalance: number;
      netFlow: number;
      analytics: {
        creditTransactionCount: number;
        debitTransactionCount: number;
        totalTransactionCount: number;
        activeWalletCount: number;
        averageWalletBalance: number;
      };
    };
  };
  exportWalletTransactions: {
    data: IWalletTransactions[];
  };
}

const initialState: ITransactionState = {
  loading: false,
  transactions: { data: [], pagination: null },
  walletTransactions: { data: [], pagination: null },
  walletAnalytics: {
    data: {
      totalFundsInflow: 0,
      totalFundsOutflow: 0,
      totalOutstandingWalletBalance: 0,
      netFlow: 0,
      analytics: {
        creditTransactionCount: 0,
        debitTransactionCount: 0,
        totalTransactionCount: 0,
        activeWalletCount: 0,
        averageWalletBalance: 0,
      },
    },
  },
  exportWalletTransactions: { data: [] },
};

export const transactionSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAllTransactions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllTransactions.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.transactions = {
        data: payload.data,
        pagination: payload.pagination,
      };
    });
    builder.addCase(getAllTransactions.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getWalletTransactions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getWalletTransactions.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.walletTransactions = {
        data: payload.data,
        pagination: payload.pagination,
      };
    });
    builder.addCase(getWalletTransactions.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(createWalletEntry.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createWalletEntry.fulfilled, (state, { payload }) => {
      state.loading = false;
      toast.success("User's wallet updated");
    });
    builder.addCase(createWalletEntry.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(fundUserWallet.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getWalletAnalytics.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getWalletAnalytics.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.walletAnalytics = payload;
    });
    builder.addCase(getWalletAnalytics.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(exportWalletTransactions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      exportWalletTransactions.fulfilled,
      (state, { payload }) => {
        state.loading = false;
        state.exportWalletTransactions = {
          data: payload.data || payload,
        };
      }
    );
    builder.addCase(exportWalletTransactions.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(fundUserWallet.fulfilled, (state, { payload }) => {
      state.loading = false;
      toast.success("User's wallet updated");
    });
    builder.addCase(fundUserWallet.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getOtp.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getOtp.fulfilled, (state, { payload }) => {
      state.loading = false;
      toast.success('OTP has been sent');
    });
    builder.addCase(getOtp.rejected, (state) => {
      state.loading = false;
    });
  },
});

const getAllTransactions = createAsyncThunk(
  'getAllTransactions',
  async (params: { page?: number; cb?: () => void; limit?: number }) => {
    try {
      const { page, cb, limit } = params;
      const query = genFilterQuery(limit);
      const { data } = await transactionsApi.getAllTransactions({
        query,
        page,
      });
      cb && cb();

      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

const getWalletTransactions = createAsyncThunk(
  'getWalletTransactions',
  async (params: { page?: number; cb?: () => void; limit?: number }) => {
    try {
      const { page, cb, limit } = params;
      const query = genFilterQuery(limit);

      const { data } = await transactionsApi.getWalletTransactions({
        query,
        page,
      });

      cb && cb();

      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

const getWalletAnalytics = createAsyncThunk(
  'getWalletAnalytics',
  async (params: { query?: string }) => {
    try {
      const { query } = params;
      const { data } = await transactionsApi.getWalletAnalytics({ query });
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

const exportWalletTransactions = createAsyncThunk(
  'exportWalletTransactions',
  async (params: { query?: string }) => {
    try {
      const { query } = params;
      const { data } = await transactionsApi.exportWalletTransactions({
        query,
      });
      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

const createWalletEntry = createAsyncThunk(
  'createWalletEntry',
  async (params: { payload: CreateWalletEntry; cb?: () => void }) => {
    try {
      const { payload, cb } = params;

      const { data } = await transactionsApi.createWalletEntry(payload);

      cb && cb();

      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

const fundUserWallet = createAsyncThunk(
  'fundUserWallet',
  async (params: { payload: FundUserWalletParams; cb?: () => void }) => {
    try {
      const { payload, cb } = params;
      const { data } = await transactionsApi.fundUserWallet(payload);

      cb && cb();

      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

const getOtp = createAsyncThunk(
  'getOtp',
  async (params: { cb?: () => void }) => {
    try {
      const { cb } = params;
      const { data } = await transactionsApi.getOtp();

      cb && cb();

      return data;
    } catch (error: any) {
      throw error;
    }
  }
);

export const transactionSelector = (state: RootState) => state.transactions;
export {
  getAllTransactions,
  getWalletTransactions,
  createWalletEntry,
  fundUserWallet,
  getOtp,
  getWalletAnalytics,
  exportWalletTransactions,
};

export default transactionSlice.reducer;
